/**
 * The responsive LAYOUT helper a scene attaches to read the live viewport. It
 * couples the Phaser Scale Manager (RESIZE mode) + the device safe-area insets to
 * the pure geometry in ./geometry.ts, and re-emits a single `change` event when the
 * viewport size/orientation shifts — so scenes relayout once, cleanly.
 *
 * Usage in a scene's create():
 *   const layout = Layout.attach(this);
 *   layout.on('change', () => this.relayout(layout));
 *   this.relayout(layout); // initial pass
 *
 * `attach` wires its own SHUTDOWN cleanup, so scenes never leak resize listeners.
 */
import Phaser from 'phaser';
import {
  anchorPoint,
  centeredBox,
  columns,
  makeSnapshot,
  responsiveColumnCount,
  type Anchor,
  type LayoutSnapshot,
  type Rect,
  type SafeAreaInsets,
} from './geometry.js';
import { readSafeAreaInsets } from './safeArea.js';

export const LAYOUT_CHANGE = 'change';

export class Layout extends Phaser.Events.EventEmitter {
  private snap: LayoutSnapshot;

  private constructor(
    private readonly scene: Phaser.Scene,
    private readonly readInsets: () => SafeAreaInsets,
  ) {
    super();
    const { width, height } = scene.scale.gameSize;
    this.snap = makeSnapshot(width, height, readInsets());
  }

  /**
   * Attach a Layout to a scene: wires the resize listener + SHUTDOWN cleanup.
   * `insetReader` is injectable for tests (defaults to the live DOM probe).
   */
  static attach(
    scene: Phaser.Scene,
    insetReader: () => SafeAreaInsets = readSafeAreaInsets,
  ): Layout {
    const layout = new Layout(scene, insetReader);
    const onResize = (): void => layout.refresh();
    scene.scale.on(Phaser.Scale.Events.RESIZE, onResize);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      scene.scale.off(Phaser.Scale.Events.RESIZE, onResize);
      layout.removeAllListeners();
    });
    return layout;
  }

  /** Re-read the size + insets; emit `change` only if something actually moved. */
  refresh(): void {
    const { width, height } = this.scene.scale.gameSize;
    const next = makeSnapshot(width, height, this.readInsets());
    if (
      next.width === this.snap.width &&
      next.height === this.snap.height &&
      next.insets.top === this.snap.insets.top &&
      next.insets.right === this.snap.insets.right &&
      next.insets.bottom === this.snap.insets.bottom &&
      next.insets.left === this.snap.insets.left
    ) {
      return;
    }
    this.snap = next;
    this.emit(LAYOUT_CHANGE, next);
  }

  // ---- Read accessors (all derive from the current snapshot) ----

  get snapshot(): LayoutSnapshot {
    return this.snap;
  }
  get width(): number {
    return this.snap.width;
  }
  get height(): number {
    return this.snap.height;
  }
  get insets(): SafeAreaInsets {
    return this.snap.insets;
  }
  get orientation(): LayoutSnapshot['orientation'] {
    return this.snap.orientation;
  }
  get isPortrait(): boolean {
    return this.snap.orientation === 'portrait';
  }
  get isCompact(): boolean {
    return this.snap.isCompact;
  }
  /** The content rect inside the safe-area insets. */
  get safe(): Rect {
    return this.snap.safe;
  }
  get centerX(): number {
    return this.snap.safe.x + this.snap.safe.width / 2;
  }
  get centerY(): number {
    return this.snap.safe.y + this.snap.safe.height / 2;
  }

  // ---- Placement helpers (delegate to the pure geometry) ----

  /** Center-point to place a `size`d object at an `anchor` of the safe rect. */
  place(
    anchor: Anchor,
    size: { width: number; height: number } = { width: 0, height: 0 },
    pad = 0,
  ): { x: number; y: number } {
    return anchorPoint(this.snap.safe, anchor, size, pad);
  }

  /** A centered modal box of up to `width` x `height`, clamped to the safe rect. */
  centeredBox(width: number, height: number, pad = 0): Rect {
    return centeredBox(this.snap.safe, width, height, pad);
  }

  /** Split the safe rect into `n` columns with a gutter. */
  columns(n: number, gutter = 0): Rect[] {
    return columns(this.snap.safe, n, gutter);
  }

  /** Column count that keeps grid tiles near `targetTileWidth` for this width. */
  responsiveColumns(targetTileWidth: number, min = 1, max = 12): number {
    return responsiveColumnCount(this.snap.safe.width, targetTileWidth, min, max);
  }
}
